import { useState, useRef, useEffect } from 'react'
import { Calendar, X } from 'lucide-react'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import type { Period } from '../api/admin-api'

interface PeriodSelectorProps {
  value: Period
  onChange: (period: Period) => void
}

const PRESET_OPTIONS = [
  { value: 7, label: '최근 7일' },
  { value: 14, label: '최근 14일' },
  { value: 30, label: '최근 30일' },
]

const MAX_RANGE_DAYS = 90

function todayStr(): string {
  return new Date().toLocaleDateString('sv-SE')
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

function diffDays(start: string, end: string): number {
  return Math.floor((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)) + 1
}

export function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [draftStart, setDraftStart] = useState('')
  const [draftEnd, setDraftEnd] = useState('')
  const selectRef = useRef<HTMLSelectElement>(null)

  const isCustom = value.type === 'custom'
  const selectValue = isCustom ? 'custom' : String(value.days)

  const today = todayStr()

  // Validation
  const validationError = (() => {
    if (!draftStart || !draftEnd) return null
    if (draftStart > draftEnd) return '시작일이 종료일보다 클 수 없습니다'
    if (diffDays(draftStart, draftEnd) > MAX_RANGE_DAYS) return `최대 ${MAX_RANGE_DAYS}일까지 조회 가능합니다`
    return null
  })()

  const canApply = draftStart && draftEnd && !validationError

  function handleSelectChange(val: string) {
    if (val === 'custom') {
      // Pre-fill with current range
      if (isCustom) {
        setDraftStart(value.startDate)
        setDraftEnd(value.endDate)
      } else {
        const end = today
        const start = new Date()
        start.setDate(start.getDate() - (value.days - 1))
        setDraftStart(start.toLocaleDateString('sv-SE'))
        setDraftEnd(end)
      }
      setPopoverOpen(true)
    } else {
      setPopoverOpen(false)
      onChange({ type: 'preset', days: Number(val) })
    }
  }

  function handleApply() {
    if (!canApply) return
    onChange({ type: 'custom', startDate: draftStart, endDate: draftEnd })
    setPopoverOpen(false)
  }

  function handleCancel() {
    setPopoverOpen(false)
    // If current value is not custom, reset select to preset value
    if (!isCustom && selectRef.current) {
      selectRef.current.value = String(value.type === 'preset' ? value.days : 14)
    }
  }

  // Sync select element when popover closes without applying
  useEffect(() => {
    if (!popoverOpen && selectRef.current) {
      selectRef.current.value = selectValue
    }
  }, [popoverOpen, selectValue])

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverAnchor asChild>
        <div className="flex flex-col items-end gap-1">
          {isCustom && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              {formatShortDate(value.startDate)} ~ {formatShortDate(value.endDate)}
              <button
                type="button"
                onClick={() => onChange({ type: 'preset', days: 14 })}
                className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                title="기본값으로 되돌리기"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <select
              ref={selectRef}
              value={selectValue}
              onChange={(e) => handleSelectChange(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-3 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
            >
              {PRESET_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              <option value="custom">직접 설정</option>
            </select>
          </div>
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="end"
        className="w-72 p-4"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Prevent closing when interacting with the select
          if (selectRef.current?.contains(e.target as Node)) {
            e.preventDefault()
          }
        }}
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">시작일</label>
            <input
              type="date"
              value={draftStart}
              max={today}
              onChange={(e) => setDraftStart(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">종료일</label>
            <input
              type="date"
              value={draftEnd}
              max={today}
              onChange={(e) => setDraftEnd(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {validationError && (
            <p className="text-xs text-red-500">{validationError}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleCancel}
              className="text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!canApply}
              className="text-sm px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              적용
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
